import React, { useState } from "react";
import authService from "../../services/authService";

const DeletePracticeInstructor = () => {
  const [identifier, setIdentifier] = useState("");
  const [searching, setSearching] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [result, setResult] = useState(null); // found practice instructor
  const [feedback, setFeedback] = useState(null); // { type, message }
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);

  const resetResult = () => {
    setResult(null);
    setShowConfirm(false);
    setConfirmChecked(false);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!identifier.trim() || searching) return;

    setSearching(true);
    setFeedback(null);
    resetResult();

    try {
      const response = await authService.lookupPracticeInstructor(
        identifier.trim(),
      );

      if (response.success) {
        if (!response.user.isPracticeInstructor) {
          setFeedback({
            type: "error",
            message:
              "The found user is not a practice instructor and cannot be deleted here.",
          });
        } else {
          setResult(response.user);
        }
      } else if (response.notFound) {
        setFeedback({
          type: "error",
          message:
            "No practice instructor with this email or username was found.",
        });
      } else {
        setFeedback({
          type: "error",
          message: response.message || "Search failed. Please try again.",
        });
      }
    } catch (error) {
      console.error("Failed to search practice instructor", error);
      setFeedback({
        type: "error",
        message: "Search failed. Please try again.",
      });
    } finally {
      setSearching(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!result || !confirmChecked || deleting) return;

    setDeleting(true);
    setFeedback(null);

    try {
      const response = await authService.deleteUser(result.id);
      if (response.success) {
        setFeedback({
          type: "success",
          message: "Practice instructor was deleted successfully.",
        });
        setIdentifier("");
        resetResult();
      } else {
        setFeedback({
          type: "error",
          message: response.message || "Practice instructor could not be deleted.",
        });
        setShowConfirm(false);
        setConfirmChecked(false);
      }
    } catch (error) {
      console.error("Failed to delete practice instructor", error);
      setFeedback({
        type: "error",
        message: "Practice instructor could not be deleted.",
      });
      setShowConfirm(false);
      setConfirmChecked(false);
    } finally {
      setDeleting(false);
    }
  };

  const fullName = result
    ? `${result.firstName || ""} ${result.lastName || ""}`.trim()
    : "";

  return (
    <div className="card-brand overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-pale-dawn bg-ice-white">
        <h3 className="font-bold text-grey-dusk flex items-center">
          <i className="fas fa-user-slash text-sunrise-red mr-3"></i>
          Delete Practice Instructor
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          Search for a practice instructor by email or username and remove the
          account from the system.
        </p>
      </div>

      <div className="p-5">
        {/* Search form */}
        <form onSubmit={handleSearch} className="space-y-2">
          <label
            htmlFor="pi-identifier"
            className="block text-sm font-semibold text-grey-dusk"
          >
            Practice instructor email or username
          </label>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              id="pi-identifier"
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="Enter email or username"
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sunrise-red/40 focus:border-sunrise-red"
            />
            <button
              type="submit"
              disabled={searching || !identifier.trim()}
              className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-sunrise-red text-white font-semibold hover:bg-sunrise-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {searching ? (
                <>
                  <i className="fas fa-circle-notch fa-spin mr-2"></i>
                  Searching...
                </>
              ) : (
                <>
                  <i className="fas fa-search mr-2"></i>
                  Search practice instructor
                </>
              )}
            </button>
          </div>
        </form>

        {/* Feedback message */}
        {feedback && (
          <div
            className={`mt-4 rounded-lg px-4 py-3 text-sm flex items-start gap-2 ${
              feedback.type === "success"
                ? "bg-green-50 text-green-700 border border-green-200"
                : "bg-red-50 text-red-600 border border-red-200"
            }`}
          >
            <i
              className={`fas ${
                feedback.type === "success"
                  ? "fa-circle-check"
                  : "fa-circle-exclamation"
              } mt-0.5`}
            ></i>
            <span>{feedback.message}</span>
          </div>
        )}

        {/* Result / empty state */}
        {result ? (
          <div className="mt-4 border border-pale-dawn rounded-xl p-4 bg-gray-50/50">
            <div className="flex items-center gap-2 mb-3">
              <i className="fas fa-user-tie text-sunrise-red"></i>
              <span className="font-bold text-grey-dusk">
                Practice instructor found
              </span>
            </div>

            <div className="space-y-1 text-sm text-gray-700">
              {fullName && (
                <div className="font-semibold text-grey-dusk">{fullName}</div>
              )}
              <div>
                <span className="text-gray-500">Username:</span>{" "}
                {result.username}
              </div>
              <div>
                <span className="text-gray-500">Email:</span> {result.email}
              </div>
              <div>
                <span className="text-gray-500">Assigned learners:</span>{" "}
                {result.assignedLearnerCount}
              </div>
            </div>

            <button
              onClick={() => {
                setShowConfirm(true);
                setConfirmChecked(false);
              }}
              className="mt-4 inline-flex items-center justify-center px-4 py-2 rounded-lg bg-white border border-red-300 text-red-600 font-semibold hover:bg-red-50 transition-colors"
            >
              <i className="fas fa-trash-alt mr-2"></i>
              Delete practice instructor
            </button>
          </div>
        ) : (
          !feedback && (
            <p className="mt-4 text-sm text-gray-500">
              Enter an email address or username to search for a practice
              instructor.
            </p>
          )
        )}
      </div>

      {/* Confirmation modal */}
      {showConfirm && result && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-brand-xl border border-pale-dawn w-full max-w-md overflow-hidden">
            <div className="px-6 py-4 border-b border-pale-dawn flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0">
                <i className="fas fa-triangle-exclamation"></i>
              </div>
              <h3 className="text-lg font-bold text-grey-dusk">
                Delete practice instructor?
              </h3>
            </div>

            <div className="px-6 py-4 space-y-4">
              <p className="text-sm text-gray-600">
                This practice instructor will be removed from the system.
                Existing learner assignments will also be removed. This action
                cannot be undone.
              </p>

              <label className="flex items-start gap-2 text-sm text-grey-dusk cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmChecked}
                  onChange={(e) => setConfirmChecked(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  I understand that this practice instructor will be deleted.
                </span>
              </label>
            </div>

            <div className="px-6 py-4 bg-gray-50 flex flex-col sm:flex-row-reverse gap-3">
              <button
                onClick={handleConfirmDelete}
                disabled={!confirmChecked || deleting}
                className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting ? (
                  <>
                    <i className="fas fa-circle-notch fa-spin mr-2"></i>
                    Deleting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-trash-alt mr-2"></i>
                    Delete practice instructor
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setShowConfirm(false);
                  setConfirmChecked(false);
                }}
                disabled={deleting}
                className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeletePracticeInstructor;
